import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { isApiError } from '@/core/http';
import {
  materialContentType,
  materialProblem,
  titleFromFilename,
  withLessonMaterials,
  type LessonMaterial,
} from '../domain/materials';
import { catalogApi } from '../infrastructure/api';
import { catalogKeys } from './queries';

const materialsKey = (courseId: string) => [...catalogKeys.all, 'materials', courseId] as const;

/** Os materiais de todas as aulas do curso, numa chamada só — cada um traz a sua aula. */
export const courseMaterialsQuery = (courseId: string) =>
  queryOptions({ queryKey: materialsKey(courseId), queryFn: () => catalogApi.courseMaterials(courseId) });

/**
 * Toda escrita num material devolve a lista da aula: ela substitui a parte
 * daquela aula na lista do curso.
 */
function useLessonMaterialMutation<V>(
  courseId: string,
  lessonId: string,
  fn: (vars: V) => Promise<LessonMaterial[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (lesson) =>
      qc.setQueryData<LessonMaterial[]>(materialsKey(courseId), (all) =>
        withLessonMaterials(all, lessonId, lesson),
      ),
  });
}

export function useAddMaterialLink(courseId: string, lessonId: string) {
  return useLessonMaterialMutation(courseId, lessonId, (body: { title: string; url: string }) =>
    catalogApi.addMaterialLink(courseId, lessonId, body),
  );
}

export function useRenameMaterial(courseId: string, lessonId: string) {
  return useLessonMaterialMutation(courseId, lessonId, ({ id, title }: { id: string; title: string }) =>
    catalogApi.renameMaterial(courseId, lessonId, id, title),
  );
}

export function useReorderMaterials(courseId: string, lessonId: string) {
  return useLessonMaterialMutation(courseId, lessonId, (ids: string[]) =>
    catalogApi.reorderMaterials(courseId, lessonId, ids),
  );
}

export function useDeleteMaterial(courseId: string, lessonId: string) {
  return useLessonMaterialMutation(courseId, lessonId, (id: string) =>
    catalogApi.deleteMaterial(courseId, lessonId, id),
  );
}

export interface MaterialUpload {
  id: number;
  name: string;
  /** 0–1 enquanto envia. */
  progress: number;
  error: string | null;
}

let uploadSeq = 0;

/**
 * Envio de arquivos de material: um por vez (fila), cada um com a sua
 * barra. Ticket → PUT direto no storage → confirma na API. O que terminou
 * sai da fila e aparece na lista; o que falhou fica com o motivo.
 */
export function useMaterialUploads(courseId: string, lessonId: string) {
  const qc = useQueryClient();
  const [uploads, setUploads] = useState<MaterialUpload[]>([]);
  const queue = useRef<Promise<void>>(Promise.resolve());

  const patch = useCallback(
    (id: number, changes: Partial<MaterialUpload>) =>
      setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...changes } : u))),
    [],
  );

  const send = useCallback(
    async (file: File, id: number) => {
      try {
        const contentType = materialContentType(file) ?? file.type;
        const ticket = await catalogApi.requestMaterialUpload(courseId, lessonId, {
          content_type: contentType,
          size_bytes: file.size,
        });
        await catalogApi.putMaterial(ticket, file, (progress) => patch(id, { progress }));
        const lesson = await catalogApi.addMaterialFile(courseId, lessonId, {
          title: titleFromFilename(file.name),
          key: ticket.key,
          filename: file.name,
          content_type: contentType,
        });
        qc.setQueryData<LessonMaterial[]>(materialsKey(courseId), (all) =>
          withLessonMaterials(all, lessonId, lesson),
        );
        setUploads((list) => list.filter((u) => u.id !== id));
      } catch (e) {
        patch(id, { error: uploadProblem(e) });
      }
    },
    [courseId, lessonId, patch, qc],
  );

  const add = useCallback(
    (files: readonly File[]) => {
      for (const file of files) {
        const id = ++uploadSeq;
        const problem = materialProblem(file);
        setUploads((list) => [...list, { id, name: file.name, progress: 0, error: problem }]);
        if (!problem) queue.current = queue.current.then(() => send(file, id));
      }
    },
    [send],
  );

  const dismiss = useCallback((id: number) => setUploads((list) => list.filter((u) => u.id !== id)), []);

  return { uploads, add, dismiss, sending: uploads.some((u) => !u.error) };
}

/** Por que o envio falhou, em português (a API responde com código). */
export function uploadProblem(e: unknown): string {
  if (!isApiError(e)) return 'Não foi possível enviar o arquivo. Tente de novo.';
  if (e.code === 'too_many_materials') return 'Esta aula já tem o máximo de materiais.';
  if (e.code === 'material_upload_missing') return 'O arquivo não chegou ao armazenamento. Envie de novo.';
  if (e.code === 'invalid_material') return 'Arquivo recusado: confira o formato e o tamanho (até 100 MB).';
  if (e.status === 403) return 'Você não tem permissão para editar este curso.';
  if (e.status === 404) return 'Esta aula não existe mais. Recarregue a página.';
  return e.message;
}
